import type { PartialEdgeBlendTarget, PartialEdgeBlendTargets } from './partial-edge-targets';
import { fusePartialBlendShapes } from './partial-edge-shape-fusion';
import { BlendCommitShapeValidator } from './BlendCommitShapeValidator';

type PuppyKernel = ReturnType<typeof import('../../../engine/loader').getPuppyKernel>;

export type FilletBuildContext = {
    definitionType: string;
    selectedRefs: string[];
    radius?: number;
    /** FilletShape 候选顺序策略。 */
    candidateOrder?: FilletShapeCandidateOrder;
};

export type EdgeFilletRegistration = {
    edgeShape: unknown;
    selectionRef: string;
    radius: number;
};

/**
 * Puppy CAD 内核 FilletShape 候选顺序。
 *
 * 设计说明：
 * 1. SolidWorks 交互里普通边圆角必须优先走稳定快路径；
 * 2. 当前分支曾把 default token 放到首位以规避二次圆角 Rational 卡顿，但它会拖慢普通边+面圆角；
 * 3. 因此候选顺序不能再是全局常量，必须由调用上下文显式声明。
 */
export type FilletShapeCandidateOrder = 'rational-first' | 'default-first';

type DisposableShape = {
    delete?: () => void;
};

export function safeDelete(value: unknown): void {
    const deleteMethod = (value as DisposableShape | null)?.delete;
    if (typeof deleteMethod !== 'function') {
        return;
    }
    try {
        deleteMethod.call(value);
    } catch {
        // Puppy CAD 内核 临时对象释放失败不能覆盖主求解错误。
    }
}

export function buildProgressRange(puppy: PuppyKernel): unknown {
    const ctor = (puppy as Record<string, unknown>).Message_ProgressRange_1 as
        | (new () => unknown)
        | undefined;
    return typeof ctor === 'function' ? new ctor() : undefined;
}

export function resolveFilletShapeCandidates(
    puppy: PuppyKernel,
    order: FilletShapeCandidateOrder = 'rational-first',
): unknown[] {
    const filletShape = (puppy as Record<string, unknown>).ChFi3d_FilletShape as Record<string, unknown> | undefined;
    const rationalFirstCandidates = [
        filletShape?.ChFi3d_Rational,
        filletShape?.ChFi3d_QuasiAngular,
        filletShape?.ChFi3d_Polynomial,
        0,
    ];
    const candidates = order === 'default-first'
        ? [
            /**
             * 二次圆角场景下，显式 Rational 候选可能在 Puppy CAD 内核 Build 内部长时间不返回。
             * default-first 只允许由调用上下文显式选择，不能影响普通边圆角快路径。
             */
            0,
            ...rationalFirstCandidates.filter((candidate) => candidate !== 0),
        ]
        : rationalFirstCandidates;
    return candidates.reduce<unknown[]>((result, candidate) => (
        candidate === undefined || result.includes(candidate) ? result : [...result, candidate]
    ), []);
}

export function resolveFilletShapeLabel(puppy: PuppyKernel, candidate: unknown): string {
    if (candidate === 0) {
        return 'default';
    }

    const filletShape = (puppy as Record<string, unknown>).ChFi3d_FilletShape as Record<string, unknown> | undefined;
    if (filletShape?.ChFi3d_Rational === candidate) {
        return 'rational';
    }
    if (filletShape?.ChFi3d_QuasiAngular === candidate) {
        return 'quasi-angular';
    }
    if (filletShape?.ChFi3d_Polynomial === candidate) {
        return 'polynomial';
    }
    return typeof candidate === 'string' ? candidate : String(candidate);
}

export function resolveBuilderInteger(
    builder: Record<string, unknown>,
    methodName: string,
    ...args: unknown[]
): number | null {
    const method = builder[methodName] as ((...input: unknown[]) => unknown) | undefined;
    if (typeof method !== 'function') {
        return null;
    }
    try {
        const value = method.call(builder, ...args);
        return typeof value === 'number' && Number.isFinite(value) ? value : null;
    } catch {
        return null;
    }
}

/** 表示 Puppy CAD 内核 圆角轮廓 Stripe 状态异常的枚举键。 */
const UNHEALTHY_FILLET_STRIPE_STATUS_KEYS = new Set([
    'ChFiDS_StartsolFailure',
    'ChFiDS_TwistedSurface',
    'ChFiDS_WalkingFailure',
    'ChFiDS_Error',
]);

/**
 * 解析 StripeStatus 对应的 ChFiDS_ErrorStatus 枚举键。
 *
 * @param puppy Puppy CAD 内核 实例。
 * @param status StripeStatus 原始返回值。
 * @returns 枚举键；无法解析时返回 null。
 */
function resolveFilletStripeStatusKey(puppy: PuppyKernel, status: unknown): string | null {
    const errorStatus = (puppy as Record<string, unknown>).ChFiDS_ErrorStatus as Record<string, unknown> | undefined;
    if (!errorStatus) {
        return null;
    }
    return Object.entries(errorStatus).find(([, candidate]) => candidate === status)?.[0] ?? null;
}

function resolveFilletStripeStatusLabel(puppy: PuppyKernel, status: unknown): string | null {
    const statusKey = resolveFilletStripeStatusKey(puppy, status);
    switch (statusKey ?? String(status)) {
        case 'ChFiDS_StartsolFailure':
            return '起始求解失败（半径可能过大或与当前几何不兼容）';
        case 'ChFiDS_TwistedSurface':
            return '曲面扭曲导致圆角求解失败';
        case 'ChFiDS_WalkingFailure':
            return '圆角过渡行走失败';
        case 'ChFiDS_Error':
            return 'Puppy CAD 内核 返回未分类圆角错误';
        case 'ChFiDS_Ok':
            return '圆角轮廓状态正常';
        default:
            return typeof status === 'string' && status.length > 0 ? status : null;
    }
}

/**
 * 判断 StripeStatus 是否表示可接受的圆角轮廓状态。
 *
 * @param puppy Puppy CAD 内核 实例。
 * @param status StripeStatus 原始返回值。
 * @returns 是否健康；仅当状态完全不可读（null/undefined）时放行，未知枚举值失败关闭。
 */
function isHealthyFilletStripeStatus(puppy: PuppyKernel, status: unknown): boolean {
    if (status === null || status === undefined) {
        return true;
    }
    const statusKey = resolveFilletStripeStatusKey(puppy, status);
    if (!statusKey) {
        // 有返回值但无法映射到 ChFiDS_ErrorStatus：失败关闭，避免把异常态当健康。
        return false;
    }
    if (statusKey === 'ChFiDS_Ok') {
        return true;
    }
    return !UNHEALTHY_FILLET_STRIPE_STATUS_KEYS.has(statusKey);
}

function collectFilletBuilderDiagnostics(
    puppy: PuppyKernel,
    builder: Record<string, unknown>,
): {
    contourCount: number | null;
    faultyContours: Array<{ contourIndex: number; status: string | null }>;
    unhealthyContours: Array<{ contourIndex: number; status: string | null }>;
    faultyVertexCount: number | null;
} {
    const stripeStatusMethod = builder.StripeStatus as ((contourIndex: number) => unknown) | undefined;
    const faultyContourCount = resolveBuilderInteger(builder, 'NbFaultyContours') ?? 0;
    const faultyContours = Array.from({ length: faultyContourCount }, (_unused, index) => {
        const contourIndex = resolveBuilderInteger(builder, 'FaultyContour', index + 1) ?? -1;
        const stripeStatus = contourIndex > 0 && typeof stripeStatusMethod === 'function'
            ? resolveFilletStripeStatusLabel(puppy, stripeStatusMethod.call(builder, contourIndex))
            : null;
        return {
            contourIndex,
            status: stripeStatus,
        };
    });

    /**
     * 超大半径等场景下，Puppy CAD 内核 可能返回 IsDone=true 且 NbFaultyContours=0，
     * 但各轮廓 StripeStatus 已暴露 StartsolFailure 等失败状态。
     * 因此必须扫描全部轮廓，而不能只依赖 FaultyContours 列表。
     */
    const contourCount = resolveBuilderInteger(builder, 'NbContours') ?? 0;
    const unhealthyContours: Array<{ contourIndex: number; status: string | null }> = [];
    if (typeof stripeStatusMethod === 'function' && contourCount > 0) {
        for (let contourIndex = 1; contourIndex <= contourCount; contourIndex += 1) {
            const stripeStatus = stripeStatusMethod.call(builder, contourIndex);
            if (!isHealthyFilletStripeStatus(puppy, stripeStatus)) {
                unhealthyContours.push({
                    contourIndex,
                    status: resolveFilletStripeStatusLabel(puppy, stripeStatus),
                });
            }
        }
    }

    return {
        contourCount,
        faultyContours,
        unhealthyContours,
        faultyVertexCount: resolveBuilderInteger(builder, 'NbFaultyVertices'),
    };
}

/**
 * 断言 Puppy CAD 内核 圆角 builder 没有返回故障轮廓/故障顶点。
 *
 * 设计说明：
 * 1. 在超大半径等输入下，MakeFillet 可能出现 IsDone=true 且可返回 Shape，
 *    但同时携带故障轮廓（FaultyContours）的“部分成功”结果；
 * 2. 这类结果写入特征树后会表现为开口、非封闭或拓扑残缺，必须在底层统一拦截；
 * 3. 这里仅负责把“带故障结果”提升为失败，详细上下文仍由上层诊断拼装函数补齐。
 *
 * @param diagnostics builder 诊断信息。
 */
function assertFilletBuilderDiagnosticsHealthy(
    diagnostics: ReturnType<typeof collectFilletBuilderDiagnostics>,
): void {
    const hasFaultyContours = diagnostics.faultyContours.length > 0;
    const hasUnhealthyContours = diagnostics.unhealthyContours.length > 0;
    const hasFaultyVertices = typeof diagnostics.faultyVertexCount === 'number'
        && diagnostics.faultyVertexCount > 0;
    if (!hasFaultyContours && !hasUnhealthyContours && !hasFaultyVertices) {
        return;
    }
    const reasonParts: string[] = [];
    if (hasFaultyContours) {
        reasonParts.push(`故障轮廓数=${diagnostics.faultyContours.length}`);
    }
    if (hasUnhealthyContours) {
        const contourSummary = diagnostics.unhealthyContours
            .map((item) => item.status
                ? `轮廓${item.contourIndex}: ${item.status}`
                : `轮廓${item.contourIndex}: 未知错误`)
            .join('；');
        reasonParts.push(`异常轮廓=${contourSummary}`);
    }
    if (hasFaultyVertices) {
        reasonParts.push(`故障顶点数=${diagnostics.faultyVertexCount}`);
    }
    throw new Error(`Puppy CAD 内核 返回带故障拓扑的部分结果（${reasonParts.join('，')}）`);
}

/**
 * 断言 MakeFillet / MakeChamfer 等同类 builder 的诊断信息健康。
 *
 * @param puppy Puppy CAD 内核 实例。
 * @param builder Puppy CAD 内核 builder。
 */
export function assertFilletLikeBuilderDiagnosticsHealthy(
    puppy: PuppyKernel,
    builder: Record<string, unknown>,
): void {
    assertFilletBuilderDiagnosticsHealthy(collectFilletBuilderDiagnostics(puppy, builder));
}

function buildFilletFailureMessage(
    reason: string,
    context: FilletBuildContext,
    diagnostics: ReturnType<typeof collectFilletBuilderDiagnostics>,
): string {
    const detailParts: string[] = [
        `类型=${context.definitionType}`,
        `边=${context.selectedRefs.length > 0 ? context.selectedRefs.join(', ') : '无'}`,
    ];
    if (typeof context.radius === 'number' && Number.isFinite(context.radius)) {
        detailParts.push(`半径=${context.radius}`);
    }
    if (typeof diagnostics.contourCount === 'number') {
        detailParts.push(`轮廓数=${diagnostics.contourCount}`);
    }
    if (typeof diagnostics.faultyVertexCount === 'number' && diagnostics.faultyVertexCount > 0) {
        detailParts.push(`故障顶点数=${diagnostics.faultyVertexCount}`);
    }
    if (diagnostics.faultyContours.length > 0) {
        const faultyContourSummary = diagnostics.faultyContours
            .map((item) => item.status
                ? `轮廓${item.contourIndex}: ${item.status}`
                : `轮廓${item.contourIndex}: 未知错误`)
            .join('；');
        detailParts.push(`故障轮廓=${faultyContourSummary}`);
    }
    if (diagnostics.unhealthyContours.length > 0) {
        const unhealthyContourSummary = diagnostics.unhealthyContours
            .map((item) => item.status
                ? `轮廓${item.contourIndex}: ${item.status}`
                : `轮廓${item.contourIndex}: 未知错误`)
            .join('；');
        detailParts.push(`异常轮廓=${unhealthyContourSummary}`);
    }
    return `圆角求解失败，${reason}（${detailParts.join('，')}）`;
}

function assertFilletBuilderResult(builder: Record<string, unknown>): unknown {
    const isDone = builder.IsDone as (() => boolean) | undefined;
    if (typeof isDone === 'function' && !isDone.call(builder)) {
        throw new Error('圆角求解失败，Puppy CAD 内核 未完成构建');
    }

    const shapeGetter = builder.Shape as (() => unknown) | undefined;
    let resultShape: unknown = null;
    let shapeError: Error | null = null;
    try {
        resultShape = typeof shapeGetter === 'function' ? shapeGetter.call(builder) : null;
    } catch (error) {
        shapeError = error instanceof Error ? error : new Error(String(error));
    }
    if (!resultShape) {
        const hasResult = builder.HasResult as (() => boolean) | undefined;
        if (typeof hasResult === 'function' && !hasResult.call(builder)) {
            throw new Error(shapeError?.message ?? '圆角求解失败，Puppy CAD 内核 未返回可用结果');
        }
        throw new Error(shapeError?.message ?? '圆角求解失败，未返回 Shape');
    }
    return resultShape;
}

export function assertFilletBuilderResultWithDiagnostics(
    puppy: PuppyKernel,
    builder: Record<string, unknown>,
    context: FilletBuildContext,
): unknown {
    const diagnostics = collectFilletBuilderDiagnostics(puppy, builder);
    try {
        const resultShape = assertFilletBuilderResult(builder);
        assertFilletBuilderDiagnosticsHealthy(diagnostics);
        return resultShape;
    } catch (error) {
        const reason = error instanceof Error && error.message.length > 0
            ? error.message
            : 'Puppy CAD 内核 未返回可用结果';
        throw new Error(buildFilletFailureMessage(
            reason,
            context,
            diagnostics,
        ));
    }
}

export function assertFilletContourAccepted(
    builder: Record<string, unknown>,
    selectionRef: string,
    edgeShape: unknown,
): void {
    const contourIndex = resolveBuilderInteger(builder, 'Contour', edgeShape);
    if (contourIndex === null) {
        return;
    }
    if (contourIndex <= 0) {
        throw new Error(`圆角求解失败，所选边不适用于当前圆角求解：${selectionRef}`);
    }
}

function isRetryableFilletFailure(error: unknown): boolean {
    const message = error instanceof Error ? error.message : String(error);
    return !message.includes('所选边不适用于当前圆角求解');
}

export function buildFilletShapeWithCandidates(input: {
    puppy: PuppyKernel;
    sourceShape: unknown;
    context: FilletBuildContext;
    registerEdges: (builder: Record<string, unknown>) => void | (() => void);
}): unknown {
    const candidateLabels: string[] = [];
    let lastError: Error | null = null;
    const makeFilletCtor = (input.puppy.BRepFilletAPI_MakeFillet as unknown) as new (
        shape: unknown,
        filletShape: unknown,
    ) => Record<string, unknown>;

    for (const candidate of resolveFilletShapeCandidates(input.puppy, input.context.candidateOrder)) {
        const builder = new makeFilletCtor(input.sourceShape, candidate);
        const progress = buildProgressRange(input.puppy);
        let registerCleanup: void | (() => void);
        candidateLabels.push(resolveFilletShapeLabel(input.puppy, candidate));
        try {
            registerCleanup = input.registerEdges(builder);
            (builder.Build as (progressArg: unknown) => void).call(builder, progress);
            const resultShape = assertFilletBuilderResultWithDiagnostics(
                input.puppy,
                builder,
                input.context,
            );
            /**
             * 候选 FilletShape 可能在过大半径下交出诊断“看起来成功”却开口的 Shape。
             * 必须在换下一候选前做封闭性闸门，避免把伪成功提前 return。
             */
            BlendCommitShapeValidator.assertValidResultShape(
                input.puppy,
                resultShape,
                'fillet',
                { sourceShape: input.sourceShape },
            );
            return resultShape;
        } catch (error) {
            if (!isRetryableFilletFailure(error)) {
                throw error;
            }
            lastError = error instanceof Error ? error : new Error(String(error));
        } finally {
            if (typeof registerCleanup === 'function') {
                registerCleanup();
            }
            safeDelete(progress);
            safeDelete(builder);
        }
    }

    throw new Error(
        `圆角求解失败，已尝试 FilletShape=${candidateLabels.join(' -> ')}，最后一次错误：${lastError?.message ?? '未知错误'}`,
    );
}

export function fusePartialFilletShapes(
    puppy: PuppyKernel,
    shapes: unknown[],
    temporaryInputShapes: unknown[],
): unknown {
    return fusePartialBlendShapes({
        puppy,
        shapes,
        temporaryInputShapes,
        operationLabel: 'Partial Edge Fillet',
    });
}

function buildFilletShapeOnSingleShape(
    puppy: PuppyKernel,
    sourceShape: unknown,
    context: FilletBuildContext,
    registrations: EdgeFilletRegistration[],
): unknown {
    return buildFilletShapeWithCandidates({
        puppy,
        sourceShape,
        context,
        registerEdges: (builder) => {
            registrations.forEach(({ edgeShape, selectionRef, radius }) => {
                (builder.Add_2 as (value: number, edge: unknown) => void).call(builder, radius, edgeShape);
                assertFilletContourAccepted(builder, selectionRef, edgeShape);
            });
        },
    });
}

export function buildPerEdgeRadiusFilletShapeOnSingleShape(input: {
    puppy: PuppyKernel;
    sourceShape: unknown;
    context: FilletBuildContext;
    registrations: EdgeFilletRegistration[];
}): unknown {
    if (input.registrations.length === 0) {
        throw new Error('圆角求解失败，缺少可注册的目标边');
    }
    return buildFilletShapeOnSingleShape(
        input.puppy,
        input.sourceShape,
        input.context,
        input.registrations,
    );
}

export function buildOrdinaryConstantSizeFilletShapeOnTargets(input: {
    puppy: PuppyKernel;
    radius: number;
    context: FilletBuildContext;
    targets: PartialEdgeBlendTargets;
}): unknown {
    const buildRegistrations = (edgeTargets: PartialEdgeBlendTarget[]): EdgeFilletRegistration[] => (
        edgeTargets.map(({ edgeShape, selection }) => ({
            edgeShape,
            selectionRef: selection.persistentRef,
            radius: input.radius,
        }))
    );

    if (input.targets.solidGroups.length === 1 && input.targets.remainderSolidShapes.length === 0) {
        return buildFilletShapeOnSingleShape(
            input.puppy,
            input.targets.solidGroups[0]!.solidShape,
            input.context,
            buildRegistrations(input.targets.solidGroups[0]!.edgeTargets),
        );
    }

    const partialResultShapes = input.targets.solidGroups.map((group) => (
        buildFilletShapeOnSingleShape(
            input.puppy,
            group.solidShape,
            input.context,
            buildRegistrations(group.edgeTargets),
        )
    ));
    return fusePartialFilletShapes(
        input.puppy,
        [...partialResultShapes, ...input.targets.remainderSolidShapes],
        partialResultShapes,
    );
}
